import React from 'react';
import { ArrowRight, Database, Zap, Code, HelpCircle, Lock } from 'lucide-react';
import { VariableSource } from '../utils/variableParser';

interface VariableTooltipProps {
  source: VariableSource;
  isUnlocked: boolean;
  variable: {
    fullMatch: string;
    path: string;
    fieldPath: string;
  };
}

/**
 * Tooltip component that displays information about a variable's data source
 * Shows the source node, data flow path, and field being referenced
 */
export const VariableTooltip: React.FC<VariableTooltipProps> = ({
  source,
  isUnlocked,
  variable,
}) => {
  // Get icon based on source type
  const getSourceIcon = () => {
    if (!source.sourceNode) {
      return <HelpCircle className="w-4 h-4 text-muted-foreground" />;
    }

    switch (source.sourceNode.data.nodeType) {
      case 'trigger':
        return <Zap className="w-4 h-4 text-amber-500" />;
      case 'action':
        return <Database className="w-4 h-4 text-blue-500" />;
      case 'code':
        return <Code className="w-4 h-4 text-purple-500" />;
      default:
        return <Database className="w-4 h-4 text-gray-500" />;
    }
  };

  // Premium content: show detailed field path
  const renderFieldPath = () => {
    if (!isUnlocked) {
      return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
          <Lock className="w-3 h-3" />
          <span>Unlock to see field details</span>
        </div>
      );
    }

    return (
      <div className="mt-2 pt-2 border-t border-border">
        <p className="text-xs text-muted-foreground mb-1">Field Path:</p>
        <code className="text-xs font-mono bg-background/50 px-2 py-1 rounded block">
          {variable.fieldPath}
        </code>
      </div>
    );
  };

  // Render the data flow path
  const renderDataPath = () => {
    if (!source.isResolvable || source.pathFromTrigger.length === 0) {
      return null;
    }

    return (
      <div className="flex items-center gap-1 text-xs mt-2">
        {source.pathFromTrigger.map((node, index) => (
          <React.Fragment key={node.id}>
            {index > 0 && <ArrowRight className="w-3 h-3 text-muted-foreground" />}
            <span className="px-2 py-1 bg-accent rounded text-accent-foreground">
              {node.data.label}
            </span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[250px] max-w-[350px]">
      {/* Header */}
      <div className="flex items-start gap-2 mb-2">
        {getSourceIcon()}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-foreground">
            {source.isResolvable ? 'Data Source' : 'Unresolved Variable'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {source.description}
          </p>
        </div>
      </div>

      {/* Variable expression */}
      <div className="mb-2">
        <p className="text-xs text-muted-foreground mb-1">Variable:</p>
        <code className="text-xs font-mono bg-muted px-2 py-1 rounded block break-all">
          {variable.fullMatch}
        </code>
      </div>

      {/* Data flow path */}
      {renderDataPath()}

      {/* Field path (premium feature) */}
      {renderFieldPath()}

      {/* Source node details (if available and unlocked) */}
      {source.sourceNode && isUnlocked && (
        <div className="mt-2 pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground mb-1">Source Node:</p>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">{source.sourceNode.data.app}</span>
            <span className="text-xs text-muted-foreground">
              {source.sourceNode.data.event}
            </span>
          </div>
        </div>
      )}

      {/* Warning for unresolved variables */}
      {!source.isResolvable && (
        <div className="mt-2 pt-2 border-t border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/20 -m-3 mt-2 p-3 rounded-b-lg">
          <p className="text-xs text-amber-800 dark:text-amber-200">
            This variable references a step that could not be found in the workflow.
          </p>
        </div>
      )}
    </div>
  );
};
